import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import styles from "./styles.module.css";
import { is_empty } from "utils/validations";
import classnames from "classnames";
import { notification_color_keys, useNotifications } from "utils/hooks";

const DraggableTable = (props) => {
  const {
    initialData,
    columnConfig,
    onDelete,
    enableDelete = false,
    showDragIcon = false,
    onReOrder = null,
    customTableStyles = {},
    CustomRow,
    disabledAreaWarningMsg,
  } = props;
  const [data, setData] = useState([]);
  const { notify } = useNotifications();

  const sanitizedData = (data) => {
    const tempObj = { ...data };
    delete tempObj._id;
    return tempObj;
  };

  useEffect(() => {
    if (!is_empty(initialData)) {
      setData(initialData);
    }
  }, [initialData]);

  const onDragEnd = (result) => {
    if (!result?.destination) {
      return;
    }

    const items = Array.from(data);
    const [reorderedItem] = items.splice(result.source.index, 1);

    // Check if destination is disabled
    if (data[result?.destination?.index]?.isDragDisabled) {
      // Find the first non-disabled position
      const firstDraggableIndex = items.findIndex(
        (item) => !item.isDragDisabled
      );
      items.splice(firstDraggableIndex, 0, reorderedItem);
      if (disabledAreaWarningMsg) {
        notify(disabledAreaWarningMsg, notification_color_keys.error);
        return;
      }
    } else {
      // Regular drop behavior
      items.splice(result.destination.index, 0, reorderedItem);
    }
    setData(items);
    if (onReOrder) onReOrder(items, data, setData);
  };

  const handleDelete = (row, id) => {
    const updatedState = data.filter(
      (item) => JSON.stringify(item) !== JSON.stringify(row)
    );
    setData(updatedState);
    if (onDelete) onDelete(id, updatedState);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <table className={classnames(styles.table, customTableStyles?.table)}>
        <thead className={classnames(styles.dtHead, customTableStyles?.thead)}>
          <tr>
            {showDragIcon && <th className={styles.defaultTd}></th>}
            {columnConfig.map((config) => (
              <th
                id={config.field}
                key={config.field}
                align={config.align}
                style={{ textAlign: config.align, width: config.width }}
              >
                {config.headerName}
              </th>
            ))}
            {enableDelete && <th></th>}
          </tr>
        </thead>
        <Droppable droppableId="table">
          {(provided) => (
            <tbody
              className={classnames(styles.dtBody, customTableStyles?.tbody)}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {data.map((rawItem, index) => {
                const item = sanitizedData(rawItem);
                const id = rawItem._id;

                return (
                  <Draggable
                    key={id}
                    draggableId={id}
                    index={index}
                    isDragDisabled={rawItem.isDragDisabled}
                  >
                    {(provided) => (
                      <tr
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        className={classnames(customTableStyles?.tr)}
                      >
                        {CustomRow ? (
                          <CustomRow item={item} {...props} />
                        ) : (
                          <>
                            {showDragIcon && (
                              <td
                                className={classnames(
                                  customTableStyles?.defaultTd,
                                  styles.defaultTd
                                )}
                              >
                                <DragIndicatorIcon
                                  className={styles.dragIcon}
                                />
                              </td>
                            )}
                            {columnConfig.map((config) => {
                              const headerCellWidth = document
                                .getElementById(config.field)
                                ?.getBoundingClientRect()?.width;
                              return (
                                <td
                                  key={`${id}-${config.field}`}
                                  className={classnames(
                                    customTableStyles?.td,
                                    config.dataColumnClassName
                                  )}
                                  style={{ minWidth: headerCellWidth }}
                                  align={config.align}
                                >
                                  {config.valueFormatter
                                    ? config.valueFormatter(item, index)
                                    : item[config.field]}
                                </td>
                              );
                            })}

                            {enableDelete && (
                              <td>
                                <IconButton
                                  onClick={() => handleDelete(rawItem, id)}
                                >
                                  <DeleteOutlineIcon
                                    className={styles.deleteButton}
                                  />
                                </IconButton>
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </table>
    </DragDropContext>
  );
};

export default DraggableTable;
